import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  LinearProgress,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Divider
} from '@mui/material';
import {
  Star as StarIcon,
  CreditCard as CreditCardIcon,
  Upgrade as UpgradeIcon,
  CheckCircle as CheckCircleIcon,
  QrCode2 as QrCodeIcon,
  AccountBalance as BankIcon,
  PhoneAndroid as PhoneIcon,
  RocketLaunch as RocketIcon
} from '@mui/icons-material';

const MySaaSPlanModal = ({ open, onClose }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [subscriptionData, setSubscriptionData] = useState(null);

  // Formulario de Pago
  const [months, setMonths] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('yape');
  const [transactionRef, setTransactionRef] = useState('');

  const fetchSubscriptionInfo = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/saas/my-subscription', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setSubscriptionData(data);
    } catch (err) {
      console.error('Error cargando suscripción SaaS:', err);
      setError('Error al consultar datos de tu suscripción');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchSubscriptionInfo();
    }
  }, [open, fetchSubscriptionInfo]);

  const handleRenewPayment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const planPrice = subscriptionData?.tenant?.plan_type === 'emprendedor' ? 50 : subscriptionData?.tenant?.plan_type === 'enterprise' ? 250 : 120;
      const totalAmount = planPrice * months;

      const res = await fetch('/api/saas/renew-subscription', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: totalAmount,
          payment_method: paymentMethod,
          billing_period_months: months,
          transaction_ref: transactionRef
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setSuccess(data.message);
      setTransactionRef('');
      fetchSubscriptionInfo();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePlan = async (newPlanType) => {
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/saas/change-plan', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ new_plan_type: newPlanType })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setSuccess(data.message);
      fetchSubscriptionInfo();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const tenant = subscriptionData?.tenant;
  const paymentMethods = subscriptionData?.payment_methods;

  const currentPlanPrice = tenant?.plan_type === 'emprendedor' ? 50 : tenant?.plan_type === 'enterprise' ? 250 : 120;
  const computedTotal = currentPlanPrice * months;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <StarIcon color="warning" />
          <Typography variant="h6" fontWeight="bold">
            Mi Suscripción YinoWISP SaaS
          </Typography>
        </Box>
        {tenant && (
          <Chip
            label={tenant.is_trial ? `PRUEBA GRATIS (${tenant.days_remaining} DÍAS)` : `${(tenant.plan_type || 'pro').toUpperCase()} (${tenant.days_remaining} DÍAS RESTANTES)`}
            color={tenant.days_remaining <= 3 ? 'error' : tenant.is_trial ? 'info' : 'success'}
            sx={{ fontWeight: 'bold' }}
          />
        )}
      </DialogTitle>

      <DialogContent divider>
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

            {/* Tarjeta de Resumen de Uso de Recursos */}
            {tenant && (
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="textSecondary" fontWeight="bold">CLIENTES CONSUMIDOS</Typography>
                      <Typography variant="body2" fontWeight="bold">{tenant.current_clients} / {tenant.max_clients}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, (tenant.current_clients / tenant.max_clients) * 100)}
                      color={(tenant.current_clients / tenant.max_clients) > 0.9 ? 'error' : 'primary'}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="textSecondary" fontWeight="bold">ROUTERS MIKROTIK CONECTADOS</Typography>
                      <Typography variant="body2" fontWeight="bold">{tenant.current_routers} / {tenant.max_routers}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, (tenant.current_routers / tenant.max_routers) * 100)}
                      color={(tenant.current_routers / tenant.max_routers) > 0.9 ? 'warning' : 'secondary'}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Paper>
                </Grid>
              </Grid>
            )}

            {/* Pestañas de Acción */}
            <Tabs
              value={tabIndex}
              onChange={(e, val) => setTabIndex(val)}
              textColor="primary"
              indicatorColor="primary"
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
              <Tab icon={<CreditCardIcon />} iconPosition="start" label="Pagar / Renovar Mensualidad" sx={{ fontWeight: 'bold' }} />
              <Tab icon={<UpgradeIcon />} iconPosition="start" label="Cambiar de Plan SaaS" sx={{ fontWeight: 'bold' }} />
            </Tabs>

            {/* Pestaña 1: Renovación y Pago */}
            {tabIndex === 0 && (
              <form onSubmit={handleRenewPayment}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" fontWeight="bold" mb={1.5}>
                      1. Selecciona la Cobertura
                    </Typography>

                    <TextField
                      fullWidth
                      select
                      label="Periodo a Contratar"
                      value={months}
                      onChange={(e) => setMonths(parseInt(e.target.value))}
                      sx={{ mb: 2 }}
                    >
                      <MenuItem value={1}>1 Mes — S/ {currentPlanPrice.toFixed(2)}</MenuItem>
                      <MenuItem value={3}>3 Meses — S/ {(currentPlanPrice * 3).toFixed(2)}</MenuItem>
                      <MenuItem value={6}>6 Meses — S/ {(currentPlanPrice * 6).toFixed(2)}</MenuItem>
                      <MenuItem value={12}>12 Meses (1 Año) — S/ {(currentPlanPrice * 12).toFixed(2)}</MenuItem>
                    </TextField>

                    <Typography variant="h5" fontWeight="800" color="primary.main" mb={2}>
                      Total a Pagar: S/ {computedTotal.toFixed(2)}
                    </Typography>

                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                      2. Método de Pago
                    </Typography>
                    <TextField
                      fullWidth
                      select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      sx={{ mb: 2 }}
                    >
                      <MenuItem value="yape">📱 Yape / Plin (Instantáneo)</MenuItem>
                      <MenuItem value="bcp">🏦 Transferencia Bancaria BCP</MenuItem>
                    </TextField>

                    <TextField
                      fullWidth
                      label="Nº de Operación / Código Yape"
                      value={transactionRef}
                      onChange={(e) => setTransactionRef(e.target.value)}
                      required
                      placeholder="Ej: 987123"
                      helperText="Ingresa el código que aparece en tu comprobante de pago"
                    />
                  </Grid>

                  {/* Instrucciones de Pago */}
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#f8fafc' }}>
                      <Typography variant="subtitle2" fontWeight="bold" mb={1} display="flex" alignItems="center" gap={1}>
                        <QrCodeIcon color="primary" /> Instrucciones de Depósito YinoWISP
                      </Typography>
                      <Divider sx={{ my: 1 }} />

                      {paymentMethod === 'yape' ? (
                        <Box py={1}>
                          <Typography variant="body2" color="textSecondary" mb={0.5}>Yapear al número:</Typography>
                          <Typography variant="h6" fontWeight="bold" color="secondary.main">{paymentMethods?.yape_plin || '987654321'}</Typography>
                          <Typography variant="caption" color="textSecondary" display="block">Titular: {paymentMethods?.yape_holder}</Typography>
                        </Box>
                      ) : (
                        <Box py={1}>
                          <Typography variant="body2" color="textSecondary" mb={0.5}>Cuenta BCP Soles:</Typography>
                          <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>{paymentMethods?.bank_account}</Typography>
                          <Typography variant="caption" color="textSecondary" display="block">CCI: {paymentMethods?.bank_cci}</Typography>
                          <Typography variant="caption" color="textSecondary" display="block">Titular: {paymentMethods?.yape_holder}</Typography>
                        </Box>
                      )}
                    </Paper>

                    <Button
                      type="submit"
                      variant="contained"
                      color="success"
                      fullWidth
                      disabled={submitting}
                      startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                      sx={{ mt: 3, py: 1.5, borderRadius: 2.5, fontWeight: 'bold', fontSize: '1rem' }}
                    >
                      {submitting ? 'Verificando...' : 'Confirmar Pago & Renovar Servicio'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}

            {/* Pestaña 2: Cambiar de Plan */}
            {tabIndex === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined" sx={{ borderRadius: 3, borderColor: tenant?.plan_type === 'emprendedor' ? '#38bdf8' : 'divider' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" fontWeight="bold">Emprendedor</Typography>
                      <Typography variant="h4" fontWeight="800" color="primary.main" my={1}>S/ 50 <Typography component="span" variant="caption">/mes</Typography></Typography>
                      <Typography variant="body2">Hasta 100 Clientes</Typography>
                      <Typography variant="body2">2 Routers MikroTik</Typography>
                      <Button
                        variant={tenant?.plan_type === 'emprendedor' ? 'outlined' : 'contained'}
                        disabled={tenant?.plan_type === 'emprendedor' || submitting}
                        onClick={() => handleChangePlan('emprendedor')}
                        sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                      >
                        {tenant?.plan_type === 'emprendedor' ? 'Plan Actual' : 'Seleccionar Plan'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Card variant="outlined" sx={{ borderRadius: 3, borderColor: tenant?.plan_type === 'pro' ? '#818cf8' : 'divider' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Chip label="POPULAR" color="primary" size="small" sx={{ mb: 0.5, fontWeight: 'bold' }} />
                      <Typography variant="h6" fontWeight="bold">Profesional</Typography>
                      <Typography variant="h4" fontWeight="800" color="primary.main" my={1}>S/ 120 <Typography component="span" variant="caption">/mes</Typography></Typography>
                      <Typography variant="body2">Hasta 500 Clientes</Typography>
                      <Typography variant="body2">5 Routers MikroTik</Typography>
                      <Button
                        variant={tenant?.plan_type === 'pro' ? 'outlined' : 'contained'}
                        disabled={tenant?.plan_type === 'pro' || submitting}
                        onClick={() => handleChangePlan('pro')}
                        sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                      >
                        {tenant?.plan_type === 'pro' ? 'Plan Actual' : 'Seleccionar Plan'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Card variant="outlined" sx={{ borderRadius: 3, borderColor: tenant?.plan_type === 'enterprise' ? '#c084fc' : 'divider' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" fontWeight="bold">Enterprise</Typography>
                      <Typography variant="h4" fontWeight="800" color="secondary.main" my={1}>S/ 250 <Typography component="span" variant="caption">/mes</Typography></Typography>
                      <Typography variant="body2">Hasta 2,000 Clientes</Typography>
                      <Typography variant="body2">15 Routers MikroTik</Typography>
                      <Button
                        variant={tenant?.plan_type === 'enterprise' ? 'outlined' : 'contained'}
                        color="secondary"
                        disabled={tenant?.plan_type === 'enterprise' || submitting}
                        onClick={() => handleChangePlan('enterprise')}
                        sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                      >
                        {tenant?.plan_type === 'enterprise' ? 'Plan Actual' : 'Seleccionar Plan'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ fontWeight: 'bold' }}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MySaaSPlanModal;
